import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { API, useAuth, authFetch } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Download, Trash2, User, Shield, FileJson, X, Plus, Camera, Heart } from "lucide-react";
import { toast } from "sonner";
import TopFivePicker from "@/components/TopFivePicker";

const GENRE_OPTIONS = [
  "Rock", "Pop", "Hip Hop", "R&B", "Electronic", "Jazz", "Classical", 
  "Metal", "Folk", "Country", "Blues", "Reggae", "Soul", "Funk", 
  "Punk", "Indie", "Alternative", "Latin", "World", "Ambient"
];

export default function Settings() {
  const { user, logout, checkAuth } = useAuth();
  const navigate = useNavigate();
  const [name, setName] = useState(user?.name || "");
  const [bio, setBio] = useState(user?.bio || "");
  const [favoriteGenres, setFavoriteGenres] = useState(user?.favorite_genres || []);
  const [saving, setSaving] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState("");
  const [deleting, setDeleting] = useState(false);
  const [uploadingAvatar, setUploadingAvatar] = useState(false);

  const handleAvatarUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) { toast.error("Image must be under 5MB"); return; }
    if (!file.type.startsWith("image/")) { toast.error("Please select an image"); return; }
    
    setUploadingAvatar(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      const res = await authFetch(`${API}/users/me/avatar`, { method: "POST", body: formData });
      if (res.ok) {
        toast.success("Profile picture updated!");
        await checkAuth();
      } else {
        const err = await res.json();
        toast.error(err.detail || "Upload failed");
      }
    } catch { toast.error("Upload failed"); }
    finally { setUploadingAvatar(false); }
  };

  const handleAddGenre = (genre) => {
    if (!favoriteGenres.includes(genre) && favoriteGenres.length < 5) {
      setFavoriteGenres([...favoriteGenres, genre]);
    }
  };

  const handleRemoveGenre = (genre) => {
    setFavoriteGenres(favoriteGenres.filter(g => g !== genre));
  };

  const handleSaveProfile = async () => {
    if (!name.trim()) {
      toast.error("Name cannot be empty");
      return;
    }
    
    setSaving(true);
    try {
      const response = await authFetch(`${API}/users/me`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ name: name.trim(), bio, favorite_genres: favoriteGenres }),
      });

      if (response.ok) {
        toast.success("Profile updated!");
        if (checkAuth) checkAuth(); // Refresh user data
      } else {
        toast.error("Failed to update profile");
      }
    } catch (error) {
      toast.error("Something went wrong");
    } finally {
      setSaving(false);
    }
  };

  const handleExportData = async (format = "json") => {
    setExporting(true);
    try {
      const response = await authFetch(`${API}/users/me/export?format=${format}`);

      if (response.ok) {
        if (format === "csv") {
          const blob = await response.blob();
          const url = URL.createObjectURL(blob);
          const a = document.createElement("a");
          a.href = url;
          a.download = `sonary-export-${new Date().toISOString().split("T")[0]}.csv`;
          document.body.appendChild(a);
          a.click();
          document.body.removeChild(a);
          URL.revokeObjectURL(url);
        } else {
          const data = await response.json();
          const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
          const url = URL.createObjectURL(blob);
          const a = document.createElement("a");
          a.href = url;
          a.download = `sonary-export-${new Date().toISOString().split("T")[0]}.json`;
          document.body.appendChild(a);
          a.click();
          document.body.removeChild(a);
          URL.revokeObjectURL(url);
        }
        toast.success("Data exported successfully!");
      } else {
        toast.error("Failed to export data");
      }
    } catch (error) {
      toast.error("Something went wrong");
    } finally {
      setExporting(false);
    }
  };

  const handleDeleteAccount = async () => {
    if (deleteConfirm !== "DELETE") {
      toast.error("Please type DELETE to confirm");
      return;
    }

    setDeleting(true);
    try {
      const response = await authFetch(`${API}/users/me`, {
        method: "DELETE",
      });

      if (response.ok) {
        toast.success("Account deleted successfully");
        await logout();
        navigate("/");
      } else {
        toast.error("Failed to delete account");
      }
    } catch (error) {
      toast.error("Something went wrong");
    } finally {
      setDeleting(false);
    }
  };

  const availableGenres = GENRE_OPTIONS.filter(g => !favoriteGenres.includes(g));

  return (
    <div className="min-h-screen bg-[#0A0510] pt-36 pb-12" data-testid="settings-page">
      {/* Purple gradient background */}
      <div className="fixed inset-0 bg-gradient-to-br from-purple-950/20 via-transparent to-purple-900/10 pointer-events-none" />
      
      <div className="relative max-w-2xl mx-auto px-4">
        <h1 className="text-3xl md:text-4xl font-bold tracking-tight mb-2 text-white">Settings</h1>
        <p className="text-white/60 mb-8">Manage your account and data</p>

        {/* Profile Section */}
        <section className="glass-card rounded-xl p-6 mb-6">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 rounded-full bg-purple-500/10 flex items-center justify-center">
              <User className="w-5 h-5 text-purple-400" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-white">Profile</h2>
              <p className="text-sm text-white/60">Update your public profile</p>
            </div>
          </div>

          <div className="space-y-4">
            {/* Avatar Upload */}
            <div className="flex items-center gap-4" data-testid="avatar-upload-section">
              <div className="relative group">
                {user?.picture ? (
                  <img
                    src={user.picture}
                    alt={user.name}
                    className="w-16 h-16 rounded-full object-cover border-2 border-purple-500/30"
                  />
                ) : (
                  <div className="w-16 h-16 rounded-full bg-purple-500/20 flex items-center justify-center text-purple-400 text-xl font-bold">
                    {user?.name?.charAt(0)?.toUpperCase()}
                  </div>
                )}
                <label className="absolute inset-0 rounded-full bg-black/50 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer">
                  <Camera className="w-5 h-5 text-white" />
                  <input
                    type="file"
                    accept="image/jpeg,image/png,image/webp,image/gif"
                    onChange={handleAvatarUpload}
                    className="hidden"
                    disabled={uploadingAvatar}
                    data-testid="avatar-file-input"
                  />
                </label>
                {uploadingAvatar && (
                  <div className="absolute inset-0 rounded-full bg-black/60 flex items-center justify-center">
                    <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  </div>
                )}
              </div>
              <div>
                <p className="text-sm font-medium text-white">Profile Picture</p>
                <p className="text-xs text-white/40">Click to upload. Max 5MB. JPEG, PNG, WebP, or GIF.</p>
              </div>
            </div>

            <div>
              <Label htmlFor="name" className="text-sm font-medium mb-2 block text-white">
                Display Name
              </Label>
              <Input
                id="name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                maxLength={50}
                className="bg-white/5 border-white/10 text-white placeholder:text-white/40 focus:border-purple-500"
                data-testid="name-input"
              />
              <p className="text-xs text-white/40 mt-1">
                This is how you'll appear to other users
              </p>
            </div>

            <div>
              <Label htmlFor="email" className="text-sm font-medium mb-2 block text-white">
                Email
              </Label>
              <Input
                id="email"
                value={user?.email || ""}
                disabled
                className="bg-white/5 border-white/10 text-white/60"
              />
              <p className="text-xs text-white/40 mt-1">
                Email cannot be changed
              </p>
            </div>

            <div>
              <Label htmlFor="bio" className="text-sm font-medium mb-2 block text-white">
                Bio
              </Label>
              <Textarea
                id="bio"
                placeholder="Tell others about your music taste..."
                value={bio}
                onChange={(e) => setBio(e.target.value)}
                maxLength={250}
                className="bg-white/5 border-white/10 text-white placeholder:text-white/40 focus:border-purple-500 min-h-[100px]"
                data-testid="bio-input"
              />
              <p className="text-xs text-white/40 mt-1">{bio.length}/250 characters</p>
            </div>

            {/* Favorite Genres */}
            <div>
              <Label className="text-sm font-medium mb-2 block text-white">
                Favorite Genres (up to 5)
              </Label>
              
              {/* Selected genres */}
              <div className="flex flex-wrap gap-2 mb-3">
                {favoriteGenres.length > 0 ? (
                  favoriteGenres.map((genre) => (
                    <Badge 
                      key={genre} 
                      className="bg-purple-500/20 text-purple-300 border-purple-500/30 gap-1 pr-1"
                    >
                      {genre}
                      <button 
                        onClick={() => handleRemoveGenre(genre)}
                        className="ml-1 hover:bg-purple-500/30 rounded-full p-0.5"
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </Badge>
                  ))
                ) : (
                  <p className="text-sm text-white/40">No genres selected</p>
                )}
              </div>
              
              {/* Available genres to add */}
              {favoriteGenres.length < 5 && (
                <div className="flex flex-wrap gap-2">
                  {availableGenres.slice(0, 10).map((genre) => (
                    <Badge 
                      key={genre}
                      variant="outline"
                      className="cursor-pointer border-white/20 text-white/60 hover:border-purple-500/50 hover:text-purple-300 transition-colors"
                      onClick={() => handleAddGenre(genre)}
                    >
                      <Plus className="w-3 h-3 mr-1" />
                      {genre}
                    </Badge>
                  ))}
                </div>
              )}
            </div>

            <Button
              onClick={handleSaveProfile}
              disabled={saving}
              className="rounded-full"
              data-testid="save-profile-btn"
            >
              {saving ? "Saving..." : "Save Changes"}
            </Button>
          </div>
        </section>

        {/* Top 5 Albums Section */}
        <section className="glass-card rounded-xl p-6 mb-6" data-testid="top-five-section">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 rounded-full bg-pink-500/10 flex items-center justify-center">
              <Heart className="w-5 h-5 text-pink-400 fill-pink-400" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-white">Top 5 Albums</h2>
              <p className="text-sm text-white/60">Curate the albums shown on your profile</p>
            </div>
          </div>
          <TopFivePicker />
        </section>

        {/* Data & Privacy Section */}
        <section className="glass-card rounded-xl p-6 mb-6">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 rounded-full bg-purple-500/10 flex items-center justify-center">
              <Shield className="w-5 h-5 text-purple-400" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-white">Data & Privacy</h2>
              <p className="text-sm text-white/60">Manage your data</p>
            </div>
          </div>

          <div className="space-y-4">
            {/* Export Data */}
            <div className="flex items-center justify-between p-4 rounded-lg bg-white/5">
              <div className="flex items-center gap-3">
                <FileJson className="w-5 h-5 text-white/50" />
                <div>
                  <p className="font-medium text-white">Export Your Data</p>
                  <p className="text-sm text-white/50">
                    Download all your data (JSON or CSV)
                  </p>
                </div>
              </div>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  onClick={() => handleExportData("json")}
                  disabled={exporting}
                  className="rounded-full border-white/20 text-white hover:bg-white/10"
                  data-testid="export-json-btn"
                >
                  <Download className="w-4 h-4 mr-2" />
                  {exporting ? "..." : "JSON"}
                </Button>
                <Button
                  variant="outline"
                  onClick={() => handleExportData("csv")}
                  disabled={exporting}
                  className="rounded-full border-white/20 text-white hover:bg-white/10"
                  data-testid="export-csv-btn"
                >
                  <Download className="w-4 h-4 mr-2" />
                  CSV
                </Button>
              </div>
            </div>

            {/* Privacy Policy Link */}
            <div className="flex items-center justify-between p-4 rounded-lg bg-white/5">
              <div className="flex items-center gap-3">
                <Shield className="w-5 h-5 text-white/50" />
                <div>
                  <p className="font-medium text-white">Privacy Policy</p>
                  <p className="text-sm text-white/50">
                    Learn how we handle your data
                  </p>
                </div>
              </div>
              <Button
                variant="outline"
                onClick={() => window.open("/privacy", "_blank")}
                className="rounded-full border-white/20 text-white hover:bg-white/10"
              >
                View
              </Button>
            </div>
          </div>
        </section>

        {/* Danger Zone */}
        <section className="glass-card rounded-xl p-6 border-red-500/20">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 rounded-full bg-red-500/10 flex items-center justify-center">
              <Trash2 className="w-5 h-5 text-red-400" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-red-400">Danger Zone</h2>
              <p className="text-sm text-white/60">Irreversible actions</p>
            </div>
          </div>

          <div className="flex items-center justify-between p-4 rounded-lg bg-red-500/5 border border-red-500/20">
            <div>
              <p className="font-medium text-white">Delete Account</p>
              <p className="text-sm text-white/50">
                Permanently delete your account and all data
              </p>
            </div>
            <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
              <DialogTrigger asChild>
                <Button variant="destructive" className="rounded-full" data-testid="delete-account-btn">
                  Delete Account
                </Button>
              </DialogTrigger>
              <DialogContent className="bg-[#1a1a2e] border-white/10">
                <DialogHeader>
                  <DialogTitle className="text-red-400">Delete Account</DialogTitle>
                  <DialogDescription className="text-white/60">
                    This action cannot be undone. This will permanently delete your account
                    and remove all your data including:
                  </DialogDescription>
                </DialogHeader>
                <ul className="list-disc pl-6 text-sm text-white/50 space-y-1 my-4">
                  <li>Your profile information</li>
                  <li>All album logs, ratings, and reviews</li>
                  <li>All lists you created</li>
                  <li>Your follows and followers</li>
                  <li>Your comments and likes</li>
                </ul>
                <div className="space-y-4">
                  <div>
                    <Label className="text-sm font-medium mb-2 block text-white">
                      Type <span className="text-red-400 font-mono">DELETE</span> to confirm
                    </Label>
                    <Input
                      value={deleteConfirm}
                      onChange={(e) => setDeleteConfirm(e.target.value)}
                      placeholder="DELETE"
                      className="bg-white/5 border-white/10 text-white placeholder:text-white/40 focus:border-red-500"
                      data-testid="delete-confirm-input"
                    />
                  </div>
                  <div className="flex gap-3">
                    <Button
                      variant="outline"
                      onClick={() => setDeleteDialogOpen(false)}
                      className="flex-1 rounded-full border-white/20 text-white hover:bg-white/10"
                    >
                      Cancel
                    </Button>
                    <Button
                      variant="destructive"
                      onClick={handleDeleteAccount}
                      disabled={deleting || deleteConfirm !== "DELETE"}
                      className="flex-1 rounded-full"
                      data-testid="confirm-delete-btn"
                    >
                      {deleting ? "Deleting..." : "Delete Forever"}
                    </Button>
                  </div>
                </div>
              </DialogContent>
            </Dialog>
          </div>
        </section>
      </div>
    </div>
  );
}
